import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

const BENNETT_LOGO_URL =
  'https://bennett2026.org.il/wp-content/uploads/2026/05/Group-174675.svg';
const CARBONATE_LOGO = '/assets/carbonate-wordmark.svg';
const CARBONATE_URL = 'https://carbonateai.com/';

/**
 * Login screen — inline email + password form, calling Cognito directly via
 * the JS SDK (no Hosted-UI redirect). On the first sign-in the user is
 * forced to set a new password; the form transitions to the new-password
 * input in-place.
 */
export function Login() {
  const { signIn, completeNewPassword, status, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bennettOk, setBennettOk] = useState(true);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'authenticated') {
      const search = new URLSearchParams(location.search);
      const next = search.get('next') ?? '/';
      navigate(next, { replace: true });
    }
  }, [status, location.search, navigate]);

  const isNewPasswordStep = status === 'new-password-required';
  const isLoading = status === 'loading';
  const displayError = localError ?? error;

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      await signIn(email.trim(), password);
    } catch {
      // error surfaces via context.error; no-op here.
    }
  };

  const onNewPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword.length < 12) {
      setLocalError('הסיסמה חייבת להכיל לפחות 12 תווים.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setLocalError('הסיסמאות לא תואמות.');
      return;
    }
    try {
      await completeNewPassword(newPassword);
    } catch {
      // error surfaces via context.error
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bennett-light-gray, #f3f5f7)',
        fontFamily: 'var(--font-sans, Heebo, sans-serif)',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          background: 'var(--bennett-white, #fff)',
          borderRadius: 16,
          padding: '32px 32px 24px',
          width: 'min(420px, 92vw)',
          boxShadow: '0 12px 30px rgba(6, 29, 46, 0.10)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 96,
            marginBottom: 8,
          }}
        >
          {bennettOk ? (
            <img
              src={BENNETT_LOGO_URL}
              alt="ביחד בראשות בנט"
              onError={() => setBennettOk(false)}
              style={{ maxHeight: 96, maxWidth: 280, width: 'auto' }}
            />
          ) : (
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-serif, "Frank Ruhl Libre", serif)',
                  fontSize: 32,
                  fontWeight: 700,
                  color: 'var(--bennett-navy, #2B286B)',
                  lineHeight: 1,
                }}
              >
                ביחד
              </div>
              <div style={{ fontSize: 14, color: 'var(--ink-3, #475569)', marginTop: 4 }}>
                בראשות בנט
              </div>
            </div>
          )}
        </div>

        {isNewPasswordStep ? (
          <form
            onSubmit={onNewPasswordSubmit}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div style={{ fontSize: 14, color: 'var(--ink-2, #334155)', marginBottom: 4 }}>
              נדרשת קביעת סיסמה חדשה לכניסה הראשונה.
            </div>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="סיסמה חדשה (12+ תווים)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={inputStyle}
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="אישור סיסמה חדשה"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              required
              style={inputStyle}
            />
            <button type="submit" disabled={isLoading} style={primaryButtonStyle(isLoading)}>
              {isLoading ? 'מעדכן…' : 'עדכן סיסמה והיכנס'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={onPasswordSubmit}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <input
              type="email"
              autoComplete="email"
              placeholder="דוא״ל"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyle}
              dir="ltr"
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="סיסמה"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
            />
            <button type="submit" disabled={isLoading} style={primaryButtonStyle(isLoading)}>
              {isLoading ? 'מתחבר…' : 'כניסה'}
            </button>
          </form>
        )}

        {displayError && (
          <p style={{ marginTop: 4, color: '#b91c1c', fontSize: 13 }}>{displayError}</p>
        )}

        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid var(--hairline, rgba(6,29,46,0.12))',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              fontSize: 11,
              letterSpacing: '0.08em',
              color: 'var(--ink-4, #9CA3AF)',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            מופעל ע״י
          </span>
          <a
            href={CARBONATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="Carbonate AI"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              opacity: 0.85,
              textDecoration: 'none',
              borderBottom: 0,
            }}
          >
            <img
              src={CARBONATE_LOGO}
              alt="Carbonate AI"
              style={{ height: 26, width: 'auto', display: 'block' }}
            />
          </a>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--hairline, #cbd5e1)',
  borderRadius: 8,
  fontSize: 15,
  fontFamily: 'inherit',
  background: '#fff',
  color: 'var(--ink-1, #0f172a)',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryButtonStyle = (loading: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '12px 16px',
  background: 'var(--bennett-teal, #12B3AE)',
  color: '#fff',
  border: 0,
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 16,
  cursor: loading ? 'wait' : 'pointer',
  opacity: loading ? 0.7 : 1,
});
